import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDateTime } from "../../utils/datetime";
import ManagementAPI from "./managementAPI";
import { Alert, PageHeader, RowActions } from "./crudkit";

const PAGE_SIZE = 20;

/**
 * PlatformContacts — super-admin view of website contact-form submissions.
 * Lists every enquiry submitted from the marketing site's Contact page, lets an
 * admin read the full message, delete a submission, and configure the recipient
 * email address that new submissions are forwarded to (default
 * reachus@younderbots.com).
 */
export default function PlatformContacts() {
    const navigate = useNavigate();
    const [rows,    setRows]    = useState([]);
    const [total,   setTotal]   = useState(0);
    const [page,    setPage]    = useState(1);
    const [search,  setSearch]  = useState("");
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState("");
    const [msg,     setMsg]     = useState("");
    const [view,    setView]    = useState(null);   // submission being read

    // Recipient-email management option
    const [recipient,   setRecipient]   = useState("");
    const [recipientEdit, setRecipientEdit] = useState("");
    const [savingRecip, setSavingRecip] = useState(false);

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const data = await ManagementAPI.listWebsiteContacts({ page, limit: PAGE_SIZE, search });
            setRows(data.contacts || []);
            setTotal(data.total || 0);
        } catch (err) {
            if (String(err.message).includes("401")) navigate("/management/login");
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadRecipient = async () => {
        try {
            const s = await ManagementAPI.getEmailSettings();
            setRecipient(s.contact_recipient || "");
            setRecipientEdit(s.contact_recipient || "");
        } catch { /* non-fatal — the table still works */ }
    };

    useEffect(() => { load(); }, [page, search]);
    useEffect(() => { loadRecipient(); }, []);

    const saveRecipient = async () => {
        setSavingRecip(true);
        setError("");
        try {
            const s = await ManagementAPI.updateEmailSettings({ contact_recipient: recipientEdit.trim() });
            setRecipient(s.contact_recipient || "");
            setRecipientEdit(s.contact_recipient || "");
            setMsg("Contact recipient updated.");
        } catch (e) {
            setError(e.message);
        } finally {
            setSavingRecip(false);
        }
    };

    const handleDelete = async (row) => {
        try {
            await ManagementAPI.deleteWebsiteContact(row.id);
            setMsg(`Submission from "${row.name}" deleted.`);
            if (view?.id === row.id) setView(null);
            load();
        } catch (e) {
            setError(e.message);
        }
    };

    const recipientDirty = recipientEdit.trim() !== (recipient || "").trim();

    return (
        <div>
            <PageHeader title="Contact Submissions" subtitle="Enquiries submitted from the marketing website's Contact page." />

            {msg   && <Alert type="success" msg={msg} onClose={() => setMsg("")} />}
            {error && <Alert type="error"   msg={error} onClose={() => setError("")} />}

            {/* Recipient email — management option */}
            <div className="mg-card" style={{ padding: "16px 18px", marginBottom: 16 }}>
                <div className="mg-form-label" style={{ marginTop: 0 }}>Forward new submissions to</div>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <input
                        className="mg-input mg-inline"
                        type="email"
                        style={{ width: 320, maxWidth: "100%" }}
                        placeholder="reachus@younderbots.com"
                        value={recipientEdit}
                        onChange={e => setRecipientEdit(e.target.value)}
                    />
                    <button className="mg-btn mg-btn-primary mg-btn-sm" onClick={saveRecipient} disabled={savingRecip || !recipientDirty}>
                        {savingRecip ? "Saving…" : "Save"}
                    </button>
                    {recipientDirty && (
                        <button className="mg-btn mg-btn-ghost mg-btn-sm" onClick={() => setRecipientEdit(recipient)} disabled={savingRecip}>
                            Reset
                        </button>
                    )}
                </div>
                <p className="mg-note" style={{ marginBottom: 0 }}>
                    Every contact-form message is emailed here (via the platform SMTP settings) and stored below. Leave blank to use the default <b>reachus@younderbots.com</b>.
                </p>
            </div>

            <div className="mg-toolbar">
                <div className="mg-field">
                    <span className="mg-field-label">Search</span>
                    <input className="mg-input mg-inline" style={{ width: 260 }} placeholder="Name, email, company or message…"
                        value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
                </div>
                {search && <button className="mg-clear" onClick={() => { setSearch(""); setPage(1); }}>Clear</button>}
                <span className="mg-count">{total} submission{total !== 1 ? "s" : ""}</span>
            </div>

            {loading ? (
                <div className="mg-loading">Loading…</div>
            ) : (
                <div className="mg-card">
                    <div className="mg-table-wrap">
                        <table className="mg-table">
                            <thead>
                                <tr>{["S.No", "Name", "Email", "Company", "Subject", "Message", "Received", "Actions"].map(h => <th key={h}>{h}</th>)}</tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 && (
                                    <tr><td colSpan={8} className="mg-empty">No contact submissions yet</td></tr>
                                )}
                                {rows.map((c, i) => (
                                    <tr key={c.id}>
                                        <td className="mg-td-muted mg-num">{(page - 1) * PAGE_SIZE + i + 1}</td>
                                        <td className="mg-td-strong">{c.name}</td>
                                        <td className="mg-td-muted">{c.email}</td>
                                        <td className="mg-td-muted">{c.company || "—"}</td>
                                        <td>{c.subject ? <span className="mg-badge neutral">{c.subject_label}</span> : "—"}</td>
                                        <td className="mg-td-muted" style={{ maxWidth: 320, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            <button className="mg-link" onClick={() => setView(c)}>{c.message}</button>
                                        </td>
                                        <td className="mg-td-muted mg-num" style={{ whiteSpace: "nowrap" }}>{formatDateTime(c.created_at)}</td>
                                        <td><RowActions onEdit={() => setView(c)} onDelete={() => handleDelete(c)} deleteLabel={`submission from "${c.name}"`} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="mg-pagination">
                <button className="mg-pagebtn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
                <span className="mg-pageinfo">Page {page}</span>
                <button className="mg-pagebtn" onClick={() => setPage(p => p + 1)} disabled={page * PAGE_SIZE >= total}>Next →</button>
            </div>

            {/* Read modal */}
            {view && (
                <div className="mg-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setView(null); }}>
                    <div className="mg-modal">
                        <div className="mg-modal-head">
                            <h3 className="mg-modal-title">Contact submission</h3>
                            <button className="mg-modal-close" onClick={() => setView(null)} aria-label="Close">×</button>
                        </div>
                        <div className="mg-modal-body">
                            <div style={{ display: "grid", gridTemplateColumns: "110px 1fr", rowGap: 8, columnGap: 12, marginBottom: 14 }}>
                                <b>Name</b><span>{view.name}</span>
                                <b>Email</b><span><a className="mg-link" href={`mailto:${view.email}`}>{view.email}</a></span>
                                <b>Company</b><span>{view.company || "—"}</span>
                                <b>Subject</b><span>{view.subject_label || "—"}</span>
                                <b>Received</b><span>{formatDateTime(view.created_at)}</span>
                            </div>
                            <b>Message</b>
                            <div className="mg-input" style={{ whiteSpace: "pre-wrap", minHeight: 100, marginTop: 6 }}>{view.message}</div>
                        </div>
                        <div className="mg-modal-foot">
                            <a className="mg-btn mg-btn-ghost" href={`mailto:${view.email}?subject=Re: ${encodeURIComponent(view.subject_label || "Your enquiry")}`}>Reply by email</a>
                            <button className="mg-btn mg-btn-primary" onClick={() => setView(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
